import React from 'react';
import { FiUser, FiVideo, FiPlay } from 'react-icons/fi';
import { getYouTubeThumbnailUrl, getYouTubeThumbnailFallbackUrl, isYouTubeUrl } from '../../utils/videoUtils';
import { urlFor } from '../../utils/sanityClient';

import { SanityImage } from '../../types/movie';

interface Person {
  _id: string;
  name: string;
  image?: SanityImage;
  imdbId?: string; // Made optional to fix TypeScript errors
}

interface Trailer {
  _id: string;
  title: string;
  videoUrl: string;
  platform: 'youtube' | 'vimeo';
  isMainTrailer: boolean;
}

interface MovieInfoProps {
  director: Person;
  directors: Person[];
  mainActors: Person[];
  trailers?: Trailer[] | null;
}

const MovieInfo: React.FC<MovieInfoProps> = ({ director, directors, mainActors, trailers }) => {
  return (
    <div className="container-custom py-12">
      <div className="grid md:grid-cols-3 gap-8">
        {/* Cast Section */}
        <div className="md:col-span-2 space-y-8">
          <section>
            <h2 className="text-3xl font-heading font-bold text-secondary-dark mb-6">
              Cast & Crew
            </h2>
            <div className="space-y-8">
              {/* Directors */}
              <div>
                <h3 className="text-xl font-semibold text-secondary-dark mb-4">Directors</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {directors.map(director => (
                    <div key={director._id} className="flex flex-col sm:flex-row items-center sm:items-start gap-4 bg-background-light/50 rounded-lg p-4">
                      <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-lg bg-background-light flex items-center justify-center shadow-md overflow-hidden">
                        {director.image ? (
                          <img
                            src={director.image ? urlFor(director.image) : ''}
                            alt={director.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <FiUser className="w-10 h-10 text-secondary" />
                        )}
                      </div>
                      <div className="text-center sm:text-left">
                        <div className="flex flex-col items-center sm:items-start">
                          <p className="text-lg font-medium text-secondary-dark">{director.name}</p>
                          <p className="text-secondary-light mt-1">Director</p>
                          {director.imdbId && (
                            <a
                              href={`https://www.imdb.com/name/${director.imdbId}/`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 p-1 hover:opacity-75 transition-opacity w-fit"
                            >
                              <img
                                src="/IMDb_Logo_Alt_Rectangle_Black._CB443386324_.png"
                                alt="IMDb"
                                className="h-6 w-auto object-contain inline-block"
                              />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Main Actors */}
              <div>
                <h3 className="text-xl font-semibold text-secondary-dark mb-4">Main Cast</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {mainActors.map(actor => (
                    <div key={actor._id} className="flex flex-col sm:flex-row items-center sm:items-start gap-4 bg-background-light/50 rounded-lg p-4">
                      <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-lg bg-background-light flex items-center justify-center shadow-md overflow-hidden">
                        {actor.image ? (
                          <img
                            src={actor.image ? urlFor(actor.image) : ''}
                            alt={actor.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <FiUser className="w-10 h-10 text-secondary" />
                        )}
                      </div>
                      <div className="text-center sm:text-left">
                        <div className="flex flex-col items-center sm:items-start">
                          <p className="text-lg font-medium text-secondary-dark">{actor.name}</p>
                          <p className="text-secondary-light mt-1">Actor</p>
                          {actor.imdbId && (
                            <a
                              href={`https://www.imdb.com/name/${actor.imdbId}/`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 p-1 hover:opacity-75 transition-opacity w-fit"
                            >
                              <img
                                src="/IMDb_Logo_Alt_Rectangle_Black._CB443386324_.png"
                                alt="IMDb"
                                className="h-6 w-auto object-contain inline-block"
                              />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Trailers Section */}
        <div>
          <section>
            <h2 className="text-2xl font-heading font-bold text-secondary-dark mb-4">
              Trailers & Videos
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2">
              {trailers && trailers.length > 0 ? (
                trailers.map(trailer => (
                  <div key={trailer._id}>
                    {isYouTubeUrl(trailer.videoUrl) ? (
                      <a
                        href={trailer.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block relative rounded-lg overflow-hidden group"
                      >
                        <img
                          src={getYouTubeThumbnailUrl(trailer.videoUrl)!}
                          alt={`${trailer.title} thumbnail`}
                          className="w-full h-auto rounded-lg"
                          onLoad={(e) => {
                            // YouTube returns a 120x90 grey placeholder (HTTP 200) when
                            // maxresdefault doesn't exist — detect it by naturalWidth
                            if (e.currentTarget.naturalWidth <= 120) {
                              const fallbackUrl = getYouTubeThumbnailFallbackUrl(trailer.videoUrl);
                              if (fallbackUrl) {
                                e.currentTarget.src = fallbackUrl;
                              }
                            }
                          }}
                          onError={(e) => {
                            const fallbackUrl = getYouTubeThumbnailFallbackUrl(trailer.videoUrl);
                            if (fallbackUrl && e.currentTarget.src !== fallbackUrl) {
                              e.currentTarget.src = fallbackUrl;
                            }
                          }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 group-hover:bg-black/60 transition-colors">
                          <div className="w-12 h-12 rounded-full bg-accent-teal/90 flex items-center justify-center">
                            <FiPlay className="w-6 h-6 text-white translate-x-0.5" />
                          </div>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                          <p className="font-medium text-white">{trailer.title}</p>
                        </div>
                      </a>
                    ) : (
                      <a
                        href={trailer.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-3 bg-background-light rounded-lg p-4 hover:bg-primary/5 transition-colors"
                      >
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <FiVideo className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-secondary-dark">{trailer.title}</p>
                          <p className="text-sm text-secondary-light">Watch now</p>
                        </div>
                      </a>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-secondary-light">
                  No trailers available yet
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MovieInfo;